import React, { useState } from 'react';
import { getApiUrl } from '@/utils/authUtils';
import { Sparkles } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { apiFetch } from '@/hooks/useFetch';
import { track } from '@/lib/analytics';

const API_URL = getApiUrl();

export const FavoriteButton = ({ phraseId, initialFavorited = false, size = 'md', onToggle }) => {
  const { user, refreshUser } = useAuth();
  const { isSpanish } = useLanguage();
  const [isFavorited, setIsFavorited] = useState(initialFavorited);
  const [isLoading, setIsLoading] = useState(false);

  // Bright aquamarine color
  const AQUAMARINE = '#00E5CC';

  const handleToggleFavorite = async (e) => {
    e.stopPropagation(); // Prevent card click
    e.preventDefault();
    
    if (!user) {
      toast.error(isSpanish ? 'Inicia sesión para guardar favoritos' : 'Sign in to save favorites');
      return;
    }

    setIsLoading(true);
    try {
      const method = isFavorited ? 'DELETE' : 'POST';
      const { error } = await apiFetch(`/api/phrases/${phraseId}/favorite`, { method });

      if (!error) {
        setIsFavorited(!isFavorited);
        onToggle?.(!isFavorited);
        
        if (!isFavorited) {
          toast.success(isSpanish ? '¡Agregado a favoritos!' : 'Added to favorites!');
          track.phraseSave(phraseId);
        } else {
          toast.info(isSpanish ? 'Eliminado de favoritos' : 'Removed from favorites');
          track.phraseUnsave(phraseId);
        }
        
        // Refresh user data to update favorite_phrases array
        refreshUser?.();
      } else {
        throw new Error('Failed to update favorite');
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
      toast.error(isSpanish ? 'Error al actualizar favorito' : 'Failed to update favorite');
    } finally {
      setIsLoading(false);
    }
  };

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const tooltipText = isSpanish
    ? 'Cuando hagas clic en esta estrella, se volverá aquamarina. Cuando esta estrella es aquamarina, significa que esta frase es una de las que te gustan (¡un favorito!)'
    : 'When you click this star it becomes aquamarine. When this star is aquamarine, it does mean this phrase is one of which you are keen (a favorite!)';

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <button
            onClick={handleToggleFavorite}
            disabled={isLoading}
            className={`
              p-1.5 rounded-full transition-all duration-200
              hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2
              ${isFavorited 
                ? 'hover:bg-[#00E5CC]/20 focus:ring-[#00E5CC]' 
                : 'hover:bg-gray-100 dark:hover:bg-gray-800 focus:ring-gray-400'
              }
              ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
            data-testid={`favorite-btn-${phraseId}`}
            aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Sparkles
              className={`${sizeClasses[size]} transition-all duration-200 ${
                isFavorited 
                  ? 'fill-[#00E5CC] text-[#00E5CC]' 
                  : 'fill-none text-[#00E5CC]'
              }`}
              strokeWidth={2}
            />
          </button>
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className="max-w-xs text-sm p-3 bg-background border shadow-lg"
        >
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default FavoriteButton;
